import { apiFetch } from './client';

// #139 — in-portal user management (bcid-admin only). Proxies the Keycloak
// Admin API through the portal API (Keycloak is loopback-only). All calls 503
// with "user management not configured" until the bcid-admin-svc credential is
// provisioned; the page renders a not-configured banner in that case.

export type BcidRole = 'bcid-admin' | 'bcid-analyst' | 'bcid-approver' | 'bcid-legal';

export interface AdminUser {
  keycloakId: string;
  username: string;
  email: string | null;
  name: string;
  role: BcidRole | null;
  enabled: boolean;
  identityProviders: string[];
  /** No federated identity link — a local password account. */
  local: boolean;
  /** Entra-linked AND holds bcid-admin: role/enable changes are blocked. */
  aadManaged: boolean;
  lastLogin: string | null;
}

export async function listUsers(): Promise<AdminUser[]> {
  const res = await apiFetch<{ items: AdminUser[] }>('/admin/users');
  return res.items;
}

export interface CreateUserInput {
  mode: 'federated' | 'local';
  email: string;
  firstName?: string;
  lastName?: string;
  role: BcidRole;
}

export interface CreateUserResult {
  keycloakId: string;
  /** Present for local creates only — the one-time temporary password. */
  tempPassword?: string;
}

export async function createUser(input: CreateUserInput, idempotencyKey: string): Promise<CreateUserResult> {
  return apiFetch<CreateUserResult>('/admin/users', {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(input),
  });
}

export async function changeUserRole(id: string, role: BcidRole): Promise<void> {
  await apiFetch<{ ok: boolean }>(`/admin/users/${encodeURIComponent(id)}/role`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  });
}

export async function setUserEnabled(id: string, enabled: boolean): Promise<void> {
  await apiFetch<{ ok: boolean }>(`/admin/users/${encodeURIComponent(id)}/enabled`, {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  });
}

/**
 * Delete an account outright (#463).
 *
 * Destroys the Keycloak account — login, credentials, sessions, federated links
 * and role memberships. The target must already be DISABLED; the API refuses an
 * enabled one with 409, because deleting cannot be undone and should not be one
 * click away from a working account.
 *
 * What survives, and the reason the local record is tombstoned rather than
 * removed: their case assignments and every audit entry naming them keep
 * rendering a name.
 */
export async function deleteUser(id: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(`/admin/users/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function sendPasswordResetEmail(id: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(`/admin/users/${encodeURIComponent(id)}/reset-password-email`, { method: 'POST' });
}

export async function resetUserTotp(id: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(`/admin/users/${encodeURIComponent(id)}/reset-totp`, { method: 'POST' });
}
